// Mock data for all pages in the Calmaro app

export interface User {
  id: string;
  name: string;
  username: string;
  avatar: string;
  joinDate: string;
  balance: number;
}

export interface Transaction {
  id: string;
  type: 'income' | 'expense';
  category: 'consultation' | 'subscription' | 'invite' | 'video' | 'task' | 'crystal' | 'onboarding';
  amount: number;
  description: string;
  date: string;
}

export interface Task {
  id: string;
  title: string;
  description: string;
  reward: number;
  completed: boolean;
  progress?: number;
  total?: number;
  emoji?: string;
  image?: string;
}

export interface JournalEntry {
  id: string;
  date: string;
  mood: 'great' | 'good' | 'okay' | 'bad' | 'terrible';
  note: string;
  activities?: string[];
}

export interface ProgressStats {
  streakDays: number;
  journalEntries: number;
  tasksCompleted: number;
  weeklyActivity: Array<{ day: string; value: number }>;
  moodTrend: Array<{ date: string; mood: number }>;
}

// Mock User Data
export const mockUser: User = {
  id: '1',
  name: 'Александра',
  username: '@alexandra_calmaro',
  avatar: '/robot.png',
  joinDate: '2025-01-15',
  balance: 2450
};

// Mock Transactions
export const mockTransactions: Transaction[] = [
  {
    id: '1',
    type: 'income',
    category: 'invite',
    amount: 500,
    description: 'Бонус за приглашение друга',
    date: '2025-10-19'
  },
  {
    id: '2',
    type: 'expense',
    category: 'consultation',
    amount: -300,
    description: 'Консультация по тревожности',
    date: '2025-10-18'
  },
  {
    id: '3',
    type: 'income',
    category: 'task',
    amount: 100,
    description: 'Выполнение ежедневного задания',
    date: '2025-10-18'
  },
  {
    id: '4',
    type: 'income',
    category: 'video',
    amount: 50,
    description: 'Просмотр обучающего видео',
    date: '2025-10-17'
  },
  {
    id: '5',
    type: 'income',
    category: 'crystal',
    amount: 200,
    description: 'Бонус за активность',
    date: '2025-10-16'
  },
  {
    id: '6',
    type: 'expense',
    category: 'subscription',
    amount: -1000,
    description: 'Месячная подписка Premium',
    date: '2025-10-15'
  }
];

// Mock Tasks
export const mockTasks: Task[] = [
  {
    id: '1',
    title: 'Утренняя медитация',
    description: 'Выполните 10-минутную медитацию',
    reward: 50,
    completed: true,
    image: '/task_1_bg.png'
  },
  {
    id: '2',
    title: 'Дыхательная практика',
    description: 'Практикуйте глубокое дыхание 5 минут',
    reward: 30,
    completed: true,
    image: '/task_2_bg.png'
  },
  {
    id: '3',
    title: 'Запись в дневник',
    description: 'Опишите свои эмоции сегодня',
    reward: 40,
    completed: false,
    image: '/task_3_bg.png'
  },
  {
    id: '4',
    title: '7 дней подряд',
    description: 'Выполните задания 7 дней подряд',
    reward: 500,
    completed: false,
    progress: 3,
    total: 7,
    image: '/task_1_bg.png'
  },
  {
    id: '5',
    title: 'Мастер медитаций',
    description: 'Выполните 20 различных медитаций',
    reward: 1000,
    completed: false,
    progress: 12,
    total: 20,
    image: '/task_2_bg.png'
  }
];

// Mock Journal Entries
export const mockJournalEntries: JournalEntry[] = [
  {
    id: '1',
    date: '2025-10-20',
    mood: 'great',
    note: 'Отличный день! Выполнила все задачи и чувствую себя энергичной.',
    activities: ['meditation', 'work']
  },
  {
    id: '2',
    date: '2025-10-19',
    mood: 'good',
    note: 'Хороший день, немного уставшая к вечеру.',
    activities: ['work', 'reading']
  },
  {
    id: '3',
    date: '2025-10-18',
    mood: 'okay',
    note: 'Обычный день, были небольшие стрессы на работе.',
    activities: ['work', 'meditation']
  },
  {
    id: '4',
    date: '2025-10-17',
    mood: 'bad',
    note: 'Чувствовала тревогу большую часть дня.',
    activities: ['work']
  },
  {
    id: '5',
    date: '2025-10-16',
    mood: 'good',
    note: 'День прошёл спокойно, успела позаниматься йогой.',
    activities: ['yoga', 'meditation', 'social']
  }
];

// Mock Progress Statistics
export const mockProgressStats: ProgressStats = {
  streakDays: 1,
  journalEntries: 15,
  tasksCompleted: 32,
  weeklyActivity: [
    { day: 'Пн', value: 3 },
    { day: 'Вт', value: 5 },
    { day: 'Ср', value: 2 },
    { day: 'Чт', value: 4 },
    { day: 'Пт', value: 6 },
    { day: 'Сб', value: 4 },
    { day: 'Вс', value: 3 }
  ],
  moodTrend: [
    { date: '14.10', mood: 4 },
    { date: '15.10', mood: 3 },
    { date: '16.10', mood: 4 },
    { date: '17.10', mood: 2 },
    { date: '18.10', mood: 3 },
    { date: '19.10', mood: 4 },
    { date: '20.10', mood: 5 }
  ]
};

// Helper functions
export const getMoodEmoji = (mood: JournalEntry['mood']): string => {
  const moodEmojis = {
    great: '😊',
    good: '🙂',
    okay: '😐',
    bad: '😟',
    terrible: '😢'
  };
  return moodEmojis[mood];
};

export const getMoodColor = (mood: JournalEntry['mood']): string => {
  const moodColors = {
    great: '#8B9556',
    good: '#A6B06A',
    okay: '#E8B896',
    bad: '#E8936A',
    terrible: '#C7522A'
  };
  return moodColors[mood];
};

export const getCategoryIcon = (category: Transaction['category']): string => {
  const icons = {
    consultation: '💬',
    subscription: '⭐',
    invite: '👥',
    video: '📹',
    task: '✅',
    crystal: '💎',
    onboarding: '🎉'
  };
  return icons[category];
};

